/**
 * ProfileNew — create a Profile. Sales-native + consent-free: enter the
 * person's date of birth and a FULL behavioral read is generated instantly
 * (no Lite state, no invite, no recipient consent).
 */

import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
import { Callout, SectionHeader, useToast } from "../components/index.js";

export default function ProfileNewPage() {
  const { session } = useSession();
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState({
    display_name: "",
    full_name: "",
    work_email: "",
    role_title: "",
    department: "",
    dob_month: "",
    dob_day: "",
    dob_year: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!session) {
      setError("Please sign in.");
      return;
    }
    if (!form.dob_month || !form.dob_day || !form.dob_year) {
      setError("Date of birth — day, month, and year — is required to generate the read.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const createRes = await fetch("/.netlify/functions/profiles", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          display_name: form.display_name,
          full_name: form.full_name || null,
          work_email: form.work_email || null,
          role_title: form.role_title || null,
          department: form.department || null,
          dob_year: Number(form.dob_year),
          dob_month: Number(form.dob_month),
          dob_day: Number(form.dob_day),
          created_via: "manual",
        }),
      });
      const created = await createRes.json();
      if (!createRes.ok) {
        setError(created.error || "Profile creation failed");
        setSubmitting(false);
        return;
      }
      toast.success("Profile created.", "The full behavioral read is ready now.");
      navigate(`/profiles/${created.internal_uuid}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 24px" }}>
      <SectionHeader index={1} title="New Profile" />

      <Callout.Tip>
        Enter the person's <strong>name and date of birth</strong> and the full
        behavioral read generates instantly — no invite, no waiting. Day, month,
        and year are all needed for the complete read.
      </Callout.Tip>

      <p
        style={{
          marginTop: 14,
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          color: "var(--ppr-color-text-secondary)",
        }}
      >
        Adding several people at once?{" "}
        <Link to="/profiles/bulk-upload">Bulk upload</Link> instead.
      </p>

      <form onSubmit={submit} style={{ display: "grid", gap: 16, marginTop: 24 }}>
        <Field label="Display name (first name preferred)" required>
          <input
            type="text" required value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            style={inputStyle}
          />
        </Field>

        <Field label="Date of birth" required>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              type="number" min="1" max="12" placeholder="MM" required
              value={form.dob_month}
              onChange={(e) => setForm({ ...form, dob_month: e.target.value })}
              style={{ ...inputStyle, width: 90 }}
              aria-label="Month"
            />
            <input
              type="number" min="1" max="31" placeholder="DD" required
              value={form.dob_day}
              onChange={(e) => setForm({ ...form, dob_day: e.target.value })}
              style={{ ...inputStyle, width: 90 }}
              aria-label="Day"
            />
            <input
              type="number" min="1900" max="2100" placeholder="YYYY" required
              value={form.dob_year}
              onChange={(e) => setForm({ ...form, dob_year: e.target.value })}
              style={{ ...inputStyle, width: 120 }}
              aria-label="Year"
            />
          </div>
        </Field>

        <Field label="Full name (optional)">
          <input
            type="text" value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            style={inputStyle}
          />
        </Field>
        <Field label="Work email (optional)">
          <input
            type="email" value={form.work_email}
            onChange={(e) => setForm({ ...form, work_email: e.target.value })}
            style={inputStyle}
          />
        </Field>
        <Field label="Role (optional)">
          <input
            type="text" value={form.role_title}
            onChange={(e) => setForm({ ...form, role_title: e.target.value })}
            style={inputStyle}
          />
        </Field>
        <Field label="Company / context (optional)">
          <input
            type="text" value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            style={inputStyle}
          />
        </Field>

        {error && (
          <div style={{
            padding: "12px 14px",
            border: "2px solid var(--ppr-color-accent-coral)",
            background: "var(--ppr-color-accent-coral-very-soft)",
            color: "var(--ppr-color-accent-coral-dark)",
          }}>
            {error}
          </div>
        )}

        <button
          type="submit" disabled={submitting}
          style={{
            padding: "14px 18px",
            fontFamily: "var(--ppr-typography-font-family-display)",
            fontSize: "var(--ppr-typography-font-size-sm)",
            letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
            textTransform: "uppercase",
            border: "1px solid var(--ppr-color-border-default)",
            background: "var(--ppr-color-accent-navy)",
            color: "var(--ppr-color-text-inverse)",
            cursor: submitting ? "wait" : "pointer",
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? "Creating…" : "Create Profile"}
        </button>
      </form>
    </div>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
};

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>
        {label}{required ? <span style={{ color: "var(--ppr-color-accent-coral)" }}> *</span> : null}
      </label>
      {children}
    </div>
  );
}
